/**
 * Image types for sections with AI-generated image support.
 *
 * Images can be either:
 * - An asset-backed image object
 * - A pending AI-generated image with workflow tracking
 */

import { readRecord, readString } from '@/lib/prop-utils';

export type ImageVariantSize = 'small' | 'medium' | 'large';

export interface ImageVariants {
  small?: string;
  medium?: string;
  large?: string;
}

interface BaseImageValue {
  url?: string;
  alt?: string;
  assetId?: string;
  folderId?: string;
  variants?: ImageVariants;
  contentType?: string;
  isInitialProp?: boolean;
}

function readImageVariants(value: unknown): ImageValue['variants'] | undefined {
  const record = readRecord(value);
  if (!record) return undefined;

  const small = readString(record.small);
  const medium = readString(record.medium);
  const large = readString(record.large);

  if (!small && !medium && !large) return undefined;

  return { small, medium, large };
}

/**
 * Asset-backed image object from section props.
 */
export interface AssetImage extends BaseImageValue {
  id?: string;
}

/**
 * Represents an AI-generated image that may still be processing.
 * Contains workflow runId for tracking and status for UI state.
 */
export interface PendingAIImage extends BaseImageValue {
  /** Mastra workflow run ID for tracking generation status */
  runId: string;
  /** Current status of image generation */
  status?: 'pending' | 'generating' | 'completed' | 'failed';
  /** Generated image URL (populated when status is 'completed') */
  url?: string;
  /** Error message if generation failed */
  error?: string;
  /** Image size/aspect ratio hint from the generation request (e.g. 'landscape_16_9') */
  imageSize?: string;
}

/**
 * Union type used for all image fields in section props.
 * Supports persisted image objects and AI-generated image placeholders.
 */
export type ImageValue = PendingAIImage | AssetImage;

/**
 * Type guard for PendingAIImage objects
 */
export function isPendingAIImage(value: unknown): value is PendingAIImage {
  if (typeof value !== 'object' || value === null) return false;

  const record = value as Record<string, unknown>;
  if (typeof record.runId === 'string') return true;
  return false;
}

function pickVariant(
  variants: ImageVariants | undefined,
  size: ImageVariantSize,
): string | undefined {
  if (!variants) return undefined;

  if (size === 'small') return variants.small;
  if (size === 'medium') return variants.medium;
  return variants.large;
}

/**
 * Extracts the image URL from an ImageValue.
 *
 * @param imageValue - Image value
 * @param size - Preferred variant size for asset-backed images
 * @returns URL string if available, undefined otherwise
 *
 * @example
 * ```typescript
 * const url = getImageUrl(item.imageUrl, 'large');
 * if (url) {
 *   return <img src={url} alt="..." />;
 * }
 * ```
 */
export function getImageUrl(
  imageValue: ImageValue | undefined | null,
  size: ImageVariantSize = 'large',
): string | undefined {
  if (!imageValue) return undefined;

  const variants = 'variants' in imageValue ? imageValue.variants : undefined;
  const variantUrl = pickVariant(variants, size);
  return variantUrl ?? imageValue.url;
}

/**
 * Extracts the alt text from an ImageValue.
 *
 * @param imageValue - Image value
 * @param fallback - Fallback alt text if none is available
 * @returns Alt text string
 */
export function getImageAlt(imageValue: ImageValue | undefined | null, fallback = ''): string {
  if (!imageValue) return fallback;
  return imageValue.alt ?? fallback;
}

/**
 * Which card an image belongs to, so AI image suggestions can use that card's
 * content instead of the whole section.
 */
export type ImageSuggestionTarget =
  // Item ids repeat across collections (templates seed `item-1`), so blockId
  // pins the lookup to the owning collection or widget.
  { kind: 'collectionItem'; blockId: string; itemId: string } | { kind: 'block'; blockId: string };

export interface SelectedAssetData {
  url: string;
  assetId: string;
  folderId?: string;
  filename?: string;
  altText?: string;
  description?: string;
  variants?: ImageVariants;
  contentType?: string;
}

export function normalizeImageValue(value: unknown): ImageValue | undefined {
  if (!value) return undefined;

  if (typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const alt = typeof record.alt === 'string' ? record.alt : undefined;
  const assetId = typeof record.assetId === 'string' ? record.assetId : undefined;
  const url = typeof record.url === 'string' ? record.url : undefined;
  const contentType = typeof record.contentType === 'string' ? record.contentType : undefined;
  const isInitialProp =
    typeof record.isInitialProp === 'boolean' ? record.isInitialProp : undefined;
  const variants = readImageVariants(record.variants);

  if (typeof record.runId === 'string') {
    return {
      runId: record.runId,
      status:
        record.status === 'pending' ||
        record.status === 'generating' ||
        record.status === 'completed' ||
        record.status === 'failed'
          ? record.status
          : undefined,
      url,
      error: typeof record.error === 'string' ? record.error : undefined,
      alt,
      assetId,
      isInitialProp,
      imageSize: typeof record.imageSize === 'string' ? record.imageSize : undefined,
    };
  }

  if (url || alt || assetId || contentType || typeof isInitialProp === 'boolean') {
    return {
      url,
      alt,
      assetId,
      variants,
      contentType,
      isInitialProp,
    };
  }

  return undefined;
}
